// talkingAvatarMath — pure length / segment / aspect-ratio helpers for the
// "Doğal konuşan video" (talking avatar) panel.
//
// The numbers here are only the studio's LIVE estimate while the author types.
// The middleware plans segments by Turkish syllables, which is more accurate
// than a word count, so whenever a /talking-avatar/credit quote for the current
// text is at hand its estimatedSeconds / segments / overLimit win (lengthInfo).
//
// capability = GET /talking-avatar/models[].capability:
//   { firstSegmentSec, extendSegmentSec, maxTotalSec, ratios[], chaining, wordsPerSecond,
//     audioDriven?, minAudioSec?, maxAudioSec? }
//
// audioDriven (OmniHuman): the narration is synthesized in the chosen voice
// first and the avatar is animated to it in ONE take (chaining 'none') — no
// segment chain and no speech-to-speech conversion.

// Ratios the studio offers, in display order. A model's native list is
// intersected with this; 1:1 is always requestable (non-native → the backend
// generates 16:9 and crops the centre square).
export const STUDIO_RATIOS = ["16:9", "9:16", "1:1"];

const DEFAULT_WORDS_PER_SECOND = 2;
const WARN_RATIO = 0.85;

const wordsPerSecond = (cap) =>
  cap && Number(cap.wordsPerSecond) > 0 ? Number(cap.wordsPerSecond) : DEFAULT_WORDS_PER_SECOND;

// Words = whitespace tokens that carry at least one letter or digit, after
// SSML/HTML tags are removed (a narration copied from the AI voice tab can
// still contain <break/> tags).
export function countWords(text) {
  if (typeof text !== "string") return 0;
  const plain = text.replace(/<[^>]*>/g, " ");
  return plain.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
}

export function estimateSeconds(text, cap) {
  const words = countWords(text);
  if (!words) return 0;
  return Math.ceil(words / wordsPerSecond(cap));
}

export const isAudioDriven = (cap) => !!(cap && cap.audioDriven);

// One call makes the whole video: no segments worth naming to the author.
export const isSingleTake = (cap) => !!cap && (isAudioDriven(cap) || cap.chaining === "none");

// Longest speech the model takes, in seconds (audio-driven: maxAudioSec).
export function maxSpeechSec(cap) {
  if (!cap) return 0;
  const audio = Number(cap.maxAudioSec);
  if (isAudioDriven(cap) && audio > 0) return audio;
  return Number(cap.maxTotalSec) > 0 ? Number(cap.maxTotalSec) : 0;
}

export function maxWords(cap) {
  if (!cap || !(Number(cap.maxTotalSec) > 0)) return 0;
  return Math.floor(Number(cap.maxTotalSec) * wordsPerSecond(cap));
}

// First segment, then extensions: est <= first ? 1 : 1 + ceil((est - first) / extend)
export function segmentCount(estSec, cap) {
  const est = Number(estSec) || 0;
  if (est <= 0 || !cap) return 0;
  const first = Number(cap.firstSegmentSec) || 0;
  const extend = Number(cap.extendSegmentSec) || first || 1;
  if (est <= first) return 1;
  return 1 + Math.ceil((est - first) / extend);
}

export function lengthMeter(estSec, maxSec) {
  const est = Number(estSec) || 0;
  const max = Number(maxSec) || 0;
  if (est <= 0) return "empty";
  if (max > 0 && est > max) return "over";
  if (max > 0 && est >= WARN_RATIO * max) return "warn";
  return "ok";
}

const isNum = (v) => typeof v === "number" && Number.isFinite(v);

// Live meter for "~41 sn / 148 sn · 96 kelime · ~6 segment". `quote` is the
// /talking-avatar/credit response for exactly this text (the caller drops
// stale quotes); its values override the local estimate.
export function lengthInfo(text, cap, quote) {
  const words = countWords(text);
  const maxSeconds = isNum(quote && quote.maxSeconds)
    ? quote.maxSeconds
    : (cap && Number(cap.maxTotalSec)) || 0;
  if (!words) {
    return {
      words: 0,
      estimatedSeconds: 0,
      maxSeconds,
      segments: 0,
      overLimit: false,
      partTooLong: false,
      state: "empty",
    };
  }
  const estimatedSeconds = isNum(quote && quote.estimatedSeconds)
    ? quote.estimatedSeconds
    : estimateSeconds(text, cap);
  const segments = isNum(quote && quote.segments)
    ? quote.segments
    : segmentCount(estimatedSeconds, cap);
  const overLimit =
    quote && typeof quote.overLimit === "boolean"
      ? quote.overLimit
      : maxSeconds > 0 && estimatedSeconds > maxSeconds;
  // Over the limit although the total fits: the planner could not split one
  // part (an over-long sentence or token — it never splits a word) into a
  // segment. The seconds then say nothing; the author must split sentences.
  const partTooLong = overLimit && maxSeconds > 0 && estimatedSeconds <= maxSeconds;
  let state = "ok";
  if (overLimit) state = "over";
  else if (maxSeconds > 0 && estimatedSeconds >= WARN_RATIO * maxSeconds) state = "warn";
  return { words, estimatedSeconds, maxSeconds, segments, overLimit, partTooLong, state };
}

export function aspectOptions(cap) {
  if (!cap) return [];
  const native = new Set(Array.isArray(cap.ratios) ? cap.ratios : []);
  return STUDIO_RATIOS.filter((r) => r === "1:1" || native.has(r)).map((value) => {
    const isNative = native.has(value);
    return { value, native: isNative, cropped: value === "1:1" && !isNative };
  });
}

// Keep the author's ratio when the (new) model can produce it; otherwise fall
// back to 16:9, then to 1:1 (always requestable).
export function coerceAspect(current, cap) {
  if (!cap) return current;
  const values = aspectOptions(cap).map((o) => o.value);
  if (values.includes(current)) return current;
  if (values.includes("16:9")) return "16:9";
  return values.includes("1:1") ? "1:1" : current;
}
